import { forwardRef, type ComponentPropsWithoutRef, type CSSProperties, type MouseEventHandler } from 'react';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import Trash02 from '@untitled-ui/icons-react/build/cjs/Trash02';
import Download02 from '@untitled-ui/icons-react/build/cjs/Download02';
import Globe01 from '@untitled-ui/icons-react/build/cjs/Globe01';
import LayersThree02 from '@untitled-ui/icons-react/build/cjs/LayersThree02';
import Sun from '@untitled-ui/icons-react/build/cjs/Sun';
import View from '@untitled-ui/icons-react/build/cjs/Eye';
import Wind02 from '@untitled-ui/icons-react/build/cjs/Wind02';
import Noise from '@untitled-ui/icons-react/build/cjs/Recording01';
import Target02 from '@untitled-ui/icons-react/build/cjs/Target02';
import Target05 from '@untitled-ui/icons-react/build/cjs/Target05';
import Target03 from '@untitled-ui/icons-react/build/cjs/Target03';
import BarChart10 from '@untitled-ui/icons-react/build/cjs/BarChart10';
import InfoCircle from '@untitled-ui/icons-react/build/cjs/InfoCircle';
import DotsVertical from '@untitled-ui/icons-react/build/cjs/DotsVertical';
import ChevronLeft from '@untitled-ui/icons-react/build/cjs/ChevronLeft';
import ChevronRight from '@untitled-ui/icons-react/build/cjs/ChevronRight';
import ChevronDown from '@untitled-ui/icons-react/build/cjs/ChevronDown';
import Edit05 from '@untitled-ui/icons-react/build/cjs/Edit05';
import Folder from '@untitled-ui/icons-react/build/cjs/Folder';
import Image03 from '@untitled-ui/icons-react/build/cjs/Image03';
import Copy01 from '@untitled-ui/icons-react/build/cjs/Copy01';
import Maximize01 from '@untitled-ui/icons-react/build/cjs/Maximize01';
import XClose from '@untitled-ui/icons-react/build/cjs/XClose';
import Check from '@untitled-ui/icons-react/build/cjs/Check';
import X from '@untitled-ui/icons-react/build/cjs/X';
import CheckSquare from '@untitled-ui/icons-react/build/cjs/CheckSquare';
import Square from '@untitled-ui/icons-react/build/cjs/Square';
import Dataflow01 from '@untitled-ui/icons-react/build/cjs/Dataflow01';
import Bookmark from '@untitled-ui/icons-react/build/cjs/Bookmark';
import RefreshCcw02 from '@untitled-ui/icons-react/build/cjs/RefreshCcw02';
import SwitchHorizontal01 from '@untitled-ui/icons-react/build/cjs/SwitchHorizontal01';
import Hourglass01 from '@untitled-ui/icons-react/build/cjs/Hourglass01';
import List from '@untitled-ui/icons-react/build/cjs/List';
import DotsGrid from '@untitled-ui/icons-react/build/cjs/DotsGrid';
import Description from '@untitled-ui/icons-react/build/cjs/File06';
import Save from '@untitled-ui/icons-react/build/cjs/Save01';
import Settings from '@untitled-ui/icons-react/build/cjs/Settings02';
import ReportIssue from '@untitled-ui/icons-react/build/cjs/Announcement03';
import MapSelection from '@untitled-ui/icons-react/build/cjs/Globe06';
import LogOut01 from '@untitled-ui/icons-react/build/cjs/LogOut01';
import Warning from '@untitled-ui/icons-react/build/cjs/AlertTriangle';
import Alert from '@untitled-ui/icons-react/build/cjs/AlertCircle';
import User01 from '@untitled-ui/icons-react/build/cjs/User01';
import Apply from '@untitled-ui/icons-react/build/cjs/ArrowBlockRight';
import Offset from '@untitled-ui/icons-react/build/cjs/StopSquare';
import FullScreen from '@untitled-ui/icons-react/build/cjs/Expand06';
import Reload from '@untitled-ui/icons-react/build/cjs/Repeat04';
import Compass from '@untitled-ui/icons-react/build/cjs/Compass';
import Back from '@untitled-ui/icons-react/build/cjs/ArrowLeft';
import ArrowRight from '@untitled-ui/icons-react/build/cjs/ArrowRight';
import Select from '@untitled-ui/icons-react/build/cjs/NavigationPointer01';
import Shadow from '@untitled-ui/icons-react/build/cjs/Contrast02';
import Upload from '@untitled-ui/icons-react/build/cjs/Upload01';
import FileAttach from '@untitled-ui/icons-react/build/cjs/FileAttachment02';
import Organization from '@untitled-ui/icons-react/build/cjs/Building05';
import Plus from '@untitled-ui/icons-react/build/cjs/Plus';

interface IconProps {
  className?: string;
  style?: CSSProperties;
  color?: string;
  size?: number | string;
  onClick?: MouseEventHandler<HTMLDivElement>;
}

const withPointer = (style: CSSProperties = {}): CSSProperties => ({
  cursor: 'pointer',
  ...style,
});

type InlineInfoIconProps = Omit<ComponentPropsWithoutRef<'span'>, 'color'> & {
  color?: string;
  size?: number | string;
};

export const WarningIcon = ({ className = 'untitled-icons', style = {}, color, size }: IconProps) => (
  <Warning
    className={className}
    style={{
      cursor: 'pointer',
      color,
      width: size,
      height: size,
      ...style,
    }}
  />
);

export const AlertIcon = ({ className = 'untitled-icons', style = {}, color, size }: IconProps) => (
  <Alert
    className={className}
    style={{
      cursor: 'pointer',
      color,
      width: size,
      height: size,
      ...style,
    }}
  />
);

export const OrganizationIcon = ({ className = 'untitled-icons', style = {} }) => (
  <Organization className={className} style={{ cursor: 'pointer', ...style }} />
);

export const PlusIcon = ({ className = 'untitled-icons', style = {} }) => (
  <Plus className={className} style={{ cursor: 'pointer', ...style }} />
);

export const LogoutIcon = ({ className = 'untitled-icons', style = {} }) => (
  <LogOut01 className={className} style={{ cursor: 'pointer', ...style }} />
);

export const UploadIcon = ({ className = 'untitled-icons', style = {} }) => (
  <Upload className={className} style={{ cursor: 'pointer', ...style }} />
);

export const FileAttachIcon = ({ className = 'untitled-icons', style = {}, onClick }: IconProps) => (
  <div
    onClick={onClick}
    style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
  >
    <FileAttach className={className} style={{ pointerEvents: 'none', ...style }} />
  </div>
);

export const BackIcon = ({ className = 'untitled-icons', style = {} }) => (
  <Back className={className} style={{ cursor: 'pointer', ...style }} />
);

export const ArrowRightIcon = ({ className = 'untitled-icons', style = {} }) => (
  <ArrowRight className={className} style={{ cursor: 'pointer', ...style }} />
);

export const AccountIcon = ({ className = 'untitled-icons', style = {} }) => (
  <User01 className={className} style={{ cursor: 'pointer', ...style }} />
);

export const ReportIssueIcon = () => <ReportIssue className="untitled-icons" style={{ cursor: 'pointer' }} />;

export const DeleteIcon = ({ className = 'untitled-icons', style = {} }) => (
  <Trash02 className={className} style={{ cursor: 'pointer', ...style }} />
);

export const SettingsIcon = ({ onClick }: IconProps) => (
  <div
    onClick={onClick}
    style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
  >
    <Settings className="untitled-icons" style={{ pointerEvents: 'none' }} />
  </div>
);

export const SaveIcon = ({ className = 'untitled-icons', style = {} }) => (
  <Save className={className} style={{ cursor: 'pointer', ...style }} />
);

export const Target05Icon = ({ className = 'untitled-icons', style = {} }) => (
  <Target05 className={className} style={{ cursor: 'pointer', ...style }} />
);

export const Target03Icon = ({ className = 'untitled-icons', style = {} }) => (
  <Target03 className={className} style={{ cursor: 'pointer', ...style }} />
);

export const DownloadIcon = () => <Download02 className="untitled-icons" style={withPointer()} />;

const GridIcon = <DotsGrid className="icon-mr" style={withPointer()} />;
const GeodataIcon = <Globe01 className="icon-mr" style={withPointer()} />;
const SimulationIcon = <LayersThree02 className="icon-mr" style={withPointer()} />;
const SolarIcon = <Sun className="icon-mr" style={withPointer()} />;
const ViewIcon = <View className="icon-mr" style={withPointer()} />;
const WindIcon = <Wind02 className="icon-mr" style={withPointer()} />;
const NoiseIcon = <Noise className="icon-mr" style={withPointer()} />;
const TargetIcon = <Target02 className="icon-mr" style={withPointer()} />;
const SavedIcon = <Bookmark className="icon-mr" style={withPointer()} />;
export const ShadowIcon = ({ className = 'untitled-icons', style = {} }: IconProps) => (
  <Shadow className={className} style={style} />
);
const MixedIcon = <Dataflow01 className="icon-mr" style={withPointer()} />;
const ObjectIcons = {
  grid: GridIcon,
  geodata: GeodataIcon,
  simulation: SimulationIcon,
  subsimulation: {
    SUN: SolarIcon,
    WIND: WindIcon,
    NOISE: NoiseIcon,
    FREEVIEW: ViewIcon,
    TARGETVIEW: TargetIcon,
    SHADOW: <ShadowIcon className="icon-mr" style={withPointer()} />,
    SAVED: SavedIcon,
    MIXED: MixedIcon,
  },
};

export type SubSimulationCategory = keyof typeof ObjectIcons.subsimulation;

interface ObjectIconItem {
  type: keyof typeof ObjectIcons;
  simType?: SubSimulationCategory;
}

export const getObjectIcon = (item: ObjectIconItem): React.ReactElement | null => {
  const { type, simType } = item;
  if (type === 'subsimulation') {
    return simType ? ObjectIcons.subsimulation[simType] : null;
  }
  return ObjectIcons[type];
};

export const getSubSimulationIcons = (category: SubSimulationCategory) => {
  return ObjectIcons.subsimulation[category];
};

export const MetricsIcon = () => <BarChart10 className="untitled-icons" style={withPointer()} />;

export const InlineInfoIcon = forwardRef<HTMLSpanElement, InlineInfoIconProps>(function InlineInfoIcon(
  { className, style = {}, color = '#4b5563', size = 16, ...props },
  ref
) {
  return (
    <span
      ref={ref}
      className={className}
      {...props}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 16,
        minWidth: 16,
        height: 16,
        minHeight: 16,
        flexShrink: 0,
        lineHeight: 1,
        ...style,
      }}
    >
      <InfoOutlinedIcon
        className={className}
        style={{
          color,
          width: size,
          height: size,
          fontSize: size,
          pointerEvents: 'none',
        }}
      />
    </span>
  );
});

export const InfoIcon = ({ onClick }: IconProps) => (
  <div onClick={onClick}>
    <InfoCircle className="untitled-icons" style={{ cursor: 'pointer' }} />
  </div>
);

export const CompassIcon = ({ onClick }: IconProps) => (
  <div onClick={onClick}>
    <Compass className="untitled-icons" style={{ cursor: 'pointer' }} />
  </div>
);

export const VerticalDotsIcon = ({ style = {} }) => (
  <DotsVertical className="untitled-icons" style={{ ...style, cursor: 'pointer' }} />
);
export const LeftDropDownArrow = ({
  visibility = 'visible',
  size = 15,
}: {
  visibility?: React.CSSProperties['visibility'];
  size?: number;
}) => <ChevronLeft style={{ cursor: 'pointer', visibility, width: size, height: size }} />;
export const RightDropDownArrow = ({
  visibility = 'visible',
  size = 15,
}: {
  visibility?: React.CSSProperties['visibility'];
  size?: number;
}) => <ChevronRight style={{ cursor: 'pointer', visibility, width: size, height: size }} />;

export const DownDropDownArrow = ({
  visibility = 'visible',
  size = 15,
}: {
  visibility?: React.CSSProperties['visibility'];
  size?: number;
}) => <ChevronDown style={{ cursor: 'pointer', visibility, width: size, height: size }} />;

export const EditIcon = ({ className = 'untitled-icons', style = {} }) => (
  <Edit05 className={className} style={{ cursor: 'pointer', ...style }} />
);

export const FolderIcon = () => <Folder className="untitled-icons" style={withPointer()} />;

export const ImageIcon = () => (
  <Image03 className="action-icons exported-files-icon" style={withPointer({ verticalAlign: 'middle' })} />
);

export const CopyIcon = () => (
  <div style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
    <Copy01 className="untitled-icons" style={{ pointerEvents: 'none' }} />
  </div>
);

export const ExpandIcon = ({ className = 'untitled-icons', style = {} }: IconProps) => (
  <Maximize01 className={className} style={{ cursor: 'pointer', pointerEvents: 'none', ...style }} />
);

export const FullScreenIcon = () => <FullScreen className="untitled-icons" style={withPointer()} />;

export const CloseIcon = ({ size = 15 }) => (
  <XClose className="untitled-icons" style={{ cursor: 'pointer', width: size, height: size }} />
);

export const SuccessIcon = ({ onClick }: IconProps) => (
  <div
    onClick={onClick}
    style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
  >
    <Check height={15} width={15} style={{ pointerEvents: 'none' }} />
  </div>
);

export const FailedIcon = ({ onClick }: IconProps) => (
  <div
    onClick={onClick}
    style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
  >
    <X height={15} width={15} style={{ pointerEvents: 'none' }} />
  </div>
);

export const TimeoutIcon = ({ onClick }: IconProps) => (
  <div
    onClick={onClick}
    style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
  >
    <Hourglass01 height={15} width={15} style={{ pointerEvents: 'none' }} />
  </div>
);

export const CheckedIcon = () => <CheckSquare height={21} width={21} style={withPointer()} />;

export const UnCheckedIcon = () => <Square height={21} width={21} style={withPointer()} />;

export const RefreshIcon = ({ onClick }: IconProps) => (
  <div onClick={onClick}>
    <RefreshCcw02 className="untitled-icons" style={{ cursor: 'pointer' }} />
  </div>
);

export const ReloadIcon = ({ className = 'untitled-icons', style = {}, onClick }: IconProps) => (
  <div
    onClick={onClick}
    style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
  >
    <Reload className={className} style={{ pointerEvents: 'none', ...style }} />
  </div>
);

export const ApplyIcon = ({ className = 'untitled-icons', style = {} }) => (
  <Apply className={className} style={{ cursor: 'pointer', ...style }} />
);

export const OffsetIcon = ({ className = 'untitled-icons', style = {} }) => (
  <Offset className={className} style={{ cursor: 'pointer', ...style }} />
);

export const SwitchUserIcon = () => <SwitchHorizontal01 className="untitled-icons" style={withPointer()} />;
export const ListViewIcon = () => <List className="untitled-icons" style={withPointer()} />;
export const GridViewIcon = () => <DotsGrid className="untitled-icons" style={withPointer()} />;
export const MapSelectionIcon = ({ className = 'untitled-icons', style = {} }) => (
  <MapSelection className={className} style={{ cursor: 'pointer', ...style }} />
);

export const SelectionIcon = ({ className = 'untitled-icons', style = {} }) => (
  <Select className={className} style={{ cursor: 'pointer', ...style }} />
);

export const DescriptionIcon = ({ onClick }: IconProps) => (
  <div onClick={onClick}>
    <Description className="untitled-icons" style={{ cursor: 'pointer' }} />
  </div>
);
